import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const StudentLoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.username?.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Query students table to find matching username
      const { data: students, error: queryError } = await supabase?.from('students')?.select('id, username, password_hash, plain_password, full_name, status, teacher_id, classroom_id')?.eq('username', formData?.username?.trim())?.maybeSingle();

      if (queryError) {
        console.error('Query error:', queryError);
        setErrors({
          submit: 'An error occurred. Please try again.'
        });
        setLoading(false);
        return;
      }

      if (!students) {
        setErrors({
          submit: 'Invalid username or password. Please check your credentials.'
        });
        setLoading(false);
        return;
      }

      // Check if student account is active
      if (students?.status !== 'active') {
        setErrors({
          submit: 'Your account is not active. Please contact your teacher.'
        });
        setLoading(false);
        return;
      }

      // Verify password using plain_password (primary method for educational app)
      let passwordValid = false;
      
      if (students?.plain_password && students?.plain_password === formData?.password) {
        passwordValid = true;
      } else {
        // Fallback: Try database function for bcrypt verification
        try {
          const { data: passwordMatch, error: passwordError } = await supabase?.rpc('verify_student_password', {
            student_username: formData?.username?.trim(),
            student_password: formData?.password
          });
          
          if (!passwordError && passwordMatch === true) {
            passwordValid = true;
          }
        } catch (rpcError) {
          console.log('RPC verification unavailable, using plain password only');
        }
      }

      if (!passwordValid) {
        setErrors({
          submit: 'Invalid username or password. Please check your credentials.'
        });
        setLoading(false);
        return;
      }

      // Update last_login_at timestamp in database
      const { error: updateError } = await supabase
        ?.from('students')
        ?.update({ last_login_at: new Date()?.toISOString() })
        ?.eq('id', students?.id);

      if (updateError) {
        console.error('Failed to update login timestamp:', updateError);
        // Don't block login if timestamp update fails
      }

      // Store student session in localStorage
      const studentSession = {
        id: students?.id,
        username: students?.username,
        fullName: students?.full_name,
        teacherId: students?.teacher_id,
        classroomId: students?.classroom_id,
        loginTime: new Date()?.toISOString()
      };
      localStorage.setItem('studentSession', JSON.stringify(studentSession));

      // Navigate to student dashboard
      navigate('/student-dashboard');
    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        submit: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">
          Sign In to Your Account
        </h2>
        <p className="text-sm text-muted-foreground">
          Use the username and password your teacher gave you
        </p>
      </div>

      {/* Demo Credentials */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-6">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-blue-900 mb-1">Try Demo Login:</p>
            <p className="text-xs text-blue-700">Username: johsmi1234 | Password: Pass1234</p>
            <p className="text-xs text-blue-700">Username: emijoh5678 | Password: Pass5678</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="relative">
          <Input
            label="Username"
            type="text"
            name="username"
            id="student-username"
            autoComplete="username"
            placeholder="Enter your username"
            value={formData?.username}
            onChange={handleChange}
            error={errors?.username}
            required
            className="text-base"
          />
          <div className="absolute right-3 top-[38px] text-blue-500">
            <Icon name="User" size={20} />
          </div>
        </div>

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            id="student-password"
            autoComplete="current-password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            required
            className="text-base"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-organic"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        {errors?.submit && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <Icon name="AlertCircle" size={20} className="text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errors?.submit}</p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={loading}
          className="mt-6 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold py-6 text-base shadow-lg"
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </Button>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Lock" size={16} className="text-green-500" />
            <span>Secure Login</span>
            <span className="mx-2">•</span>
            <Icon name="Shield" size={16} className="text-green-500" />
            <span>Safe & Protected</span>
          </div>
        </div>
      </form>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Lightbulb" size={18} className="text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-yellow-900 mb-1">Can't remember your login?</p>
            <p className="text-xs text-yellow-700">Ask your teacher for help with your username and password.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentLoginForm;